/**
 * SortableAdminRow — a flat Admin list row that can be dragged into a new order
 * by its grip (Common Links, Credit Types, Email Header & Footer). The page wraps
 * its table in a DndContext (useAdminDragSensors) and a SortableContext of the
 * row ids, and puts ADMIN_DRAG_COLUMN first while the list can be reordered —
 * in the account's order, with no column sorted, no search and no deleted rows.
 *
 * On a Locked Admin page the row never drags and shows no grip. Its grip cell
 * stays (empty) while the page asks for it, so the row still lines up under a
 * page's ADMIN_DRAG_COLUMN; a page drops that column by also checking
 * useAdminPageLevel().
 */
import type { ReactNode } from 'react';
import { KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AdminListColumn } from './AdminList';
import { useAdminPageLevel } from './AdminPageLevelContext';

/** The grip column, shown while the list can be reordered. */
export const ADMIN_DRAG_COLUMN: AdminListColumn = { key: 'drag', header: <span className="sr-only">Order</span>, className: 'w-10' };

/** A short pointer move starts a drag, so a click still opens the row. */
export function useAdminDragSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

export function SortableAdminRow({
  id,
  title,
  canDrag,
  className,
  onClick,
  children,
}: {
  id: number;
  /** For the grip's accessible name: "Drag to reorder {title}". */
  title: string;
  canDrag: boolean;
  className: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  const locked = useAdminPageLevel() === 'locked';
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !canDrag || locked,
  });
  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(className, isDragging && 'relative z-10 bg-white shadow-md')}
      data-row-id={id}
      onClick={onClick}
    >
      {canDrag && (
        <td className="w-10 px-2 py-3">
          {!locked && (
            <button
              type="button"
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              onClick={(e) => e.stopPropagation()}
              className="cursor-grab touch-none text-slate-300 hover:text-slate-500 active:cursor-grabbing"
              aria-label={`Drag to reorder ${title}`}
              title="Drag to reorder"
            >
              <GripVertical className="h-4 w-4" aria-hidden />
            </button>
          )}
        </td>
      )}
      {children}
    </tr>
  );
}
